import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { DICT } from "./i18n";

const LangCtx = createContext(null);
const AuthCtx = createContext(null);
const CartCtx = createContext(null);

export function LangProvider({ children }) {
  const initial = new URLSearchParams(window.location.search).get("lang") === "bm" ? "bm" : localStorage.getItem("ddv_lang") || "en";
  const [lang, setLangState] = useState(initial);
  const setLang = (l) => {
    localStorage.setItem("ddv_lang", l);
    setLangState(l);
  };
  useEffect(() => {
    document.documentElement.lang = lang === "bm" ? "ms" : "en";
  }, [lang]);
  const t = useCallback((k, vars) => {
    let s = DICT[lang][k] ?? DICT.en[k] ?? k;
    if (vars) Object.entries(vars).forEach(([a, b]) => (s = s.replace(`{${a}}`, b)));
    return s;
  }, [lang]);
  const pick = useCallback((obj, field) => (lang === "bm" && obj?.[`${field}_bm`]) || obj?.[field], [lang]);
  return <LangCtx.Provider value={{ lang, setLang, t, pick }}>{children}</LangCtx.Provider>;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, setUser, loading, refresh, logout }}>{children}</AuthCtx.Provider>;
}

const load = (k, d) => {
  try {
    return JSON.parse(localStorage.getItem(k)) || d;
  } catch (e) {
    return d;
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("ddv_cart", []));
  const [games, setGames] = useState(() => load("ddv_cart_games", []));
  const [open, setOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem("ddv_cart", JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    localStorage.setItem("ddv_cart_games", JSON.stringify(games));
  }, [games]);
  const add = (line) => {
    setItems((cur) => {
      const ex = cur.find((i) => i.variant_id === line.variant_id);
      if (ex) return cur.map((i) => (i.variant_id === line.variant_id ? { ...i, qty: Math.min(i.qty + line.qty, line.max || 20) } : i));
      return [...cur, line];
    });
    setOpen(true);
  };
  const update = (vid, qty) => setItems((cur) => cur.map((i) => (i.variant_id === vid ? { ...i, qty: Math.max(1, Math.min(qty, i.max || 20)) } : i)));
  const remove = (vid) => setItems((cur) => cur.filter((i) => i.variant_id !== vid));
  const addGame = (g) => {
    setGames((cur) => [...cur, { ...g, key: `${g.game_product_id}-${Date.now()}` }]);
    setOpen(true);
  };
  const removeGame = (key) => setGames((cur) => cur.filter((g) => g.key !== key));
  const clear = () => {
    setItems([]);
    setGames([]);
  };
  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0) + games.reduce((s, g) => s + g.price, 0);
    const count = items.reduce((s, i) => s + i.qty, 0) + games.length;
    return { items, games, add, update, remove, addGame, removeGame, clear, subtotal, count, open, setOpen };
  }, [items, games, open]); // eslint-disable-line react-hooks/exhaustive-deps
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
export const useAuth = () => useContext(AuthCtx);
export const useCart = () => useContext(CartCtx);
